// Short Pay: a driver says their pay was short, their manager raises it here,
// and Luyanda approves and pays the difference online. db/migrations/8003.
//
// The driver need not still be active: a leaver whose last payslip was short is
// owed it all the same. A decline goes back to the manager with its reason, so
// the driver hears it from the person they told, and can take it to a grievance.
//
// Paid here is paid outside SimplePay. The page says so where Luyanda pays,
// because it still has to reach the payroll records (docs/PAYMENT-REQUESTS.md).
// Every figure and refusal on this page is Postgres's. CLAUDE.md rule 4.
import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Check, ChevronDown, ChevronRight, Coins, X } from 'lucide-react'
import { useDecideShortPay, useGetShortPay, useRaiseShortPay } from '../hooks/backend/shortPay'
import { Button } from '../lib/shadcn/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../lib/shadcn/dialog'
import { Input } from '../lib/shadcn/input'
import { NativeSelect } from '../lib/shadcn/native-select'
import { Spinner } from '../lib/shadcn/spinner'
import { Textarea } from '../lib/shadcn/textarea'
import { Chip } from '../components/interviews/Chip'
import { PaymentFiles, type PaymentFile } from '../components/payments/PaymentFiles'
import { BANKS_FALLBACK, ErrorNote, PageGate, REQUEST_TONE, Row, STATUS_FILTERS, type RequestStatus } from '../components/payments/requestParts'
import { formatDay, formatWhen, formatZar, localPhone } from '../utils/loans'

type ShortPayClaim = {
  id: string; ref: string; status: RequestStatus; driver_id: string; driver_name: string | null
  pay_date: string; amount_zar: number; driver_says: string; manager_check: string
  payout_method: 'ewallet' | 'bank'; payout_text: string
  requested_by_name: string | null; requested_at: string
  approved_at: string | null; approved_by_name: string | null
  declined_at: string | null; decline_reason: string | null
  paid_at: string | null; paid_by_name: string | null; paid_note: string | null
  cancelled_at: string | null; cancel_reason: string | null
  files: PaymentFile[]
}

type ShortPayList = {
  can_raise: boolean; can_release: boolean; releasers: string; today: string; banks: string[] | null
  drivers: { id: string; name: string; status: string; manager: string | null; phone: string | null }[] | null
  rows: ShortPayClaim[]
  counts: { to_approve: number; to_pay: number; paid_this_month: number; paid_this_month_zar: number }
}

const STATUS_LABEL: Record<RequestStatus, string> = {
  requested: 'Waiting for approval',
  approved: 'Approved, to pay',
  paid: 'Paid',
  declined: 'Declined',
  cancelled: 'Cancelled',
}

export default function ShortPay() {
  const list = useGetShortPay<ShortPayList | null>()
  const [params] = useSearchParams()
  const deepLink = params.get('r')
  const [status, setStatus] = useState(deepLink ? '' : 'open')
  const [driver, setDriver] = useState('')
  const [expanded, setExpanded] = useState<string | null>(deepLink)
  const [raising, setRaising] = useState(false)
  // data starts null, which is also the answer for a login that may not see
  // this page, so the first answer is waited for explicitly.
  const [settled, setSettled] = useState(false)

  const load = list.trigger
  const refresh = useCallback(() => {
    void load({ status: status || null, driver_id: driver || null }, { skipCache: true })
      .catch(() => undefined).finally(() => setSettled(true))
  }, [load, status, driver])
  useEffect(() => { refresh() }, [refresh])

  const data = list.data ?? null
  const releasers = data?.releasers ?? 'Luyanda'

  return (
    <PageGate
      loading={!settled}
      error={data === null ? list.error : null}
      denied={settled && data === null && !list.error && !list.loading}
      onRetry={refresh}
      deniedTitle="Short pay claims are open to driver managers and the people who run pay."
      deniedHint="If you should be one of them, ask an admin to switch on Edit drivers for you in cockpit access."
    >
      <div className="flex h-full min-h-0 flex-col bg-card">
        <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <Coins className="h-5 w-5" />
          </div>
          <div className="min-w-[12rem] flex-1">
            <h1 className="text-lg font-semibold text-foreground">Short Pay</h1>
            <p className="text-xs text-muted-foreground">
              When a driver says their pay was short, raise it here with how you checked it. {releasers} approves and pays the difference online.
            </p>
          </div>
          {data?.can_raise && (
            <Button variant="brand" size="sm" onClick={() => setRaising(true)}>
              <Coins className="mr-1 h-4 w-4" /> Raise a short pay claim
            </Button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2">
          {data?.counts && (
            <>
              <Chip tone={data.counts.to_approve ? 'amber' : 'neutral'}>{data.counts.to_approve} waiting for approval</Chip>
              <Chip tone={data.counts.to_pay ? 'brand' : 'neutral'}>{data.counts.to_pay} approved, to pay</Chip>
              <Chip tone="neutral">{data.counts.paid_this_month} paid this month, {formatZar(data.counts.paid_this_month_zar)}</Chip>
            </>
          )}
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <NativeSelect className="h-8 w-44 text-xs" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
              {STATUS_FILTERS('Approved, to pay').map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
            </NativeSelect>
            <NativeSelect className="h-8 w-48 text-xs" value={driver} onChange={(e) => setDriver(e.target.value)} aria-label="Driver">
              <option value="">Every driver</option>
              {(data?.drivers ?? []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </NativeSelect>
          </div>
        </div>

        {list.error && data !== null && <div className="border-b border-border px-4 py-2"><ErrorNote>{list.error}</ErrorNote></div>}

        <div className="min-h-0 flex-1 overflow-y-auto">
          {(data?.rows ?? []).length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-muted-foreground">
              {status === 'open' && !driver ? 'No short pay claim is open.' : 'No short pay claim matches these filters.'}
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {(data?.rows ?? []).map((r) => (
                <ShortPayItem key={r.id} claim={r} open={expanded === r.id}
                  onToggle={() => setExpanded((x) => (x === r.id ? null : r.id))}
                  canRelease={data?.can_release === true} canRaise={data?.can_raise === true}
                  releasers={releasers} onChanged={refresh} />
              ))}
            </ul>
          )}
        </div>

        <RaiseShortPayDialog open={raising} onOpenChange={setRaising} data={data} onSaved={refresh} />
      </div>
    </PageGate>
  )
}

function ShortPayItem({ claim: r, open, onToggle, canRelease, canRaise, releasers, onChanged }: {
  claim: ShortPayClaim; open: boolean; onToggle: () => void; canRelease: boolean; canRaise: boolean
  releasers: string; onChanged: () => void
}) {
  const decide = useDecideShortPay()
  const [error, setError] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const [asking, setAsking] = useState<'decline' | 'cancel' | null>(null)
  const [why, setWhy] = useState('')
  const unpaid = r.status === 'requested' || r.status === 'approved'

  async function act(action: 'approve' | 'decline' | 'paid' | 'cancel', text = '') {
    setError(null)
    try {
      await decide.trigger({ id: r.id, action, text })
      setAsking(null); setWhy('')
      onChanged()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  return (
    <li>
      <button type="button" onClick={onToggle} className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 text-left hover:bg-accent/40">
        {open ? <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
        <span className="w-16 shrink-0 font-mono text-xs text-muted-foreground">{r.ref}</span>
        <span className="min-w-[10rem] flex-1">
          <span className="block text-sm font-medium text-foreground">{r.driver_name ?? '—'}</span>
          <span className="block text-xs text-muted-foreground">Payslip of {formatDay(r.pay_date)}</span>
        </span>
        <span className="w-24 text-right text-sm font-semibold tabular-nums text-foreground">{formatZar(r.amount_zar)}</span>
        <span className="flex w-full justify-end sm:w-auto"><Chip tone={REQUEST_TONE[r.status]}>{STATUS_LABEL[r.status]}</Chip></span>
      </button>

      {open && (
        <div className="space-y-3 bg-muted/20 px-4 pb-4 pl-11 text-sm">
          <dl className="grid gap-x-6 gap-y-1 text-xs sm:grid-cols-2">
            <Row k="The driver says" v={r.driver_says} />
            <Row k="How it was checked" v={r.manager_check} />
            <Row k="Payslip of" v={formatDay(r.pay_date)} />
            <Row k="Pay by" v={r.payout_text} />
            <Row k="Raised by" v={`${r.requested_by_name ?? '—'}, ${formatWhen(r.requested_at)}`} />
            {r.approved_at && <Row k="Approved" v={`${formatWhen(r.approved_at)}${r.approved_by_name ? ` by ${r.approved_by_name}` : ''}`} />}
            {r.paid_at && <Row k="Paid" v={`${formatWhen(r.paid_at)} by ${r.paid_by_name ?? '—'}${r.paid_note ? `: ${r.paid_note}` : ''}`} />}
            {r.declined_at && <Row k="Declined" v={`${formatWhen(r.declined_at)}: ${r.decline_reason ?? ''}`} />}
            {r.cancelled_at && <Row k="Cancelled" v={`${formatWhen(r.cancelled_at)}: ${r.cancel_reason ?? ''}`} />}
          </dl>

          {unpaid && canRelease && (
            <div className="space-y-2 rounded-md border border-[hsl(var(--brand))] bg-[hsl(var(--brand-soft))] p-3 text-xs text-[hsl(var(--brand-soft-foreground))]">
              <p className="text-sm font-semibold">Pay {formatZar(r.amount_zar)} by {r.payout_text}.</p>
              <p>This is pay paid outside SimplePay, so it is not on the payslip or the IRP5 until payroll carries it there.</p>
              <span className="flex flex-wrap items-center gap-1">
                <Input className="h-8 w-60 bg-background text-xs" placeholder="Optional note, e.g. EFT from FNB" value={note} onChange={(e) => setNote(e.target.value)} />
                <Button size="sm" variant="brand" disabled={decide.loading} onClick={() => void act('paid', note)}>
                  {decide.loading ? <Spinner className="mr-1 h-3.5 w-3.5" /> : <Check className="mr-1 h-3.5 w-3.5" />}
                  {r.status === 'requested' ? 'Approve and mark paid' : 'I have paid'}
                </Button>
                {r.status === 'requested' && (
                  <Button size="sm" variant="outline" className="bg-background" disabled={decide.loading} onClick={() => void act('approve')}>Approve, pay later</Button>
                )}
                {asking !== 'decline' && <Button size="sm" variant="ghost" onClick={() => setAsking('decline')}>Decline</Button>}
              </span>
            </div>
          )}
          {unpaid && !canRelease && (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950 dark:text-amber-100">
              {r.status === 'requested' ? `Waiting for ${releasers} to approve it.` : `Approved. Waiting for ${releasers} to pay it.`} You will be emailed when it is paid or declined.
            </p>
          )}

          <div className="space-y-1">
            <span className="text-xs font-medium text-foreground">Files</span>
            {r.files.length === 0 && <p className="text-xs text-muted-foreground">No files yet. A payslip or a bank statement helps {releasers} decide.</p>}
            <PaymentFiles kind="shortpay" requestId={r.id} files={r.files} onChanged={onChanged}
              uploadRoles={[
                ...((canRaise || canRelease) && r.status !== 'cancelled' ? [{ role: 'evidence' as const, label: 'Upload payslip or statement' }] : []),
                ...(canRelease ? [{ role: 'proof' as const, label: 'Upload proof of payment' }] : []),
              ]} />
          </div>

          {unpaid && (canRaise || canRelease) && asking === null && (
            <Button size="sm" variant="ghost" onClick={() => setAsking('cancel')}>Cancel this claim</Button>
          )}
          {asking && (
            <span className="flex flex-wrap items-center gap-1">
              <Input className="h-8 w-64 text-xs" placeholder={asking === 'decline' ? 'Why it is declined (the manager reads this)' : 'Why it is being cancelled'} value={why} onChange={(e) => setWhy(e.target.value)} />
              <Button size="sm" variant="destructive" disabled={decide.loading} onClick={() => void act(asking, why)}>
                {asking === 'decline' ? 'Decline it' : 'Cancel it'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setAsking(null)}><X className="h-3.5 w-3.5" /></Button>
            </span>
          )}
          {error && <ErrorNote>{error}</ErrorNote>}
        </div>
      )}
    </li>
  )
}

function RaiseShortPayDialog({ open, onOpenChange, data, onSaved }: {
  open: boolean; onOpenChange: (o: boolean) => void; data: ShortPayList | null; onSaved: () => void
}) {
  const raise = useRaiseShortPay<{ ok: boolean; id: string; ref: string }>()
  const [driverId, setDriverId] = useState('')
  const [payDate, setPayDate] = useState('')
  const [amount, setAmount] = useState('')
  const [says, setSays] = useState('')
  const [check, setCheck] = useState('')
  const [method, setMethod] = useState<'ewallet' | 'bank'>('bank')
  const [ewallet, setEwallet] = useState('')
  const [bank, setBank] = useState('')
  const [holder, setHolder] = useState('')
  const [account, setAccount] = useState('')
  const [branch, setBranch] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState<string | null>(null)

  // Reset only when the dialog opens, so a background refresh never wipes what
  // the manager typed.
  useEffect(() => {
    if (!open) return
    setDriverId(''); setPayDate(''); setAmount(''); setSays(''); setCheck('')
    setMethod('bank'); setEwallet(''); setBank(''); setHolder(''); setAccount(''); setBranch('')
    setError(null); setSaved(null)
  }, [open])

  const drivers = data?.drivers ?? []

  function chooseDriver(id: string) {
    setDriverId(id)
    const d = drivers.find((x) => x.id === id)
    if (!d) return
    setHolder(d.name)
    setEwallet(localPhone(d.phone))
  }

  async function save() {
    setError(null)
    try {
      const r = await raise.trigger({
        driver_id: driverId || null, pay_date: payDate || null, amount_zar: amount.replace(',', '.').trim(),
        driver_says: says, manager_check: check, payout_method: method,
        ewallet_number: method === 'ewallet' ? ewallet : null,
        bank_name: method === 'bank' ? bank : null,
        account_holder: method === 'bank' ? holder : null,
        account_number: method === 'bank' ? account : null,
        branch_code: method === 'bank' ? branch : null,
      })
      if (r) { setSaved(r.ref); onSaved() }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  const releasers = data?.releasers ?? 'Luyanda'

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!raise.loading) onOpenChange(o) }}>
      <DialogContent className="max-h-[92vh] max-w-lg overflow-y-auto">
        {saved ? (
          <>
            <DialogHeader>
              <DialogTitle>{saved} is with {releasers}</DialogTitle>
              <DialogDescription>
                {releasers} has been emailed to approve and pay it. You will be emailed when it is paid or declined. Add the payslip or a bank statement to it on the list if you have one.
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-end"><Button variant="brand" onClick={() => onOpenChange(false)}>Done</Button></div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Raise a short pay claim</DialogTitle>
              <DialogDescription>For a driver who says their pay was less than it should have been. {releasers} approves and pays the difference.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <div className="space-y-1">
                <label htmlFor="sp-driver" className="text-xs font-medium text-foreground">Driver</label>
                <NativeSelect id="sp-driver" value={driverId} onChange={(e) => chooseDriver(e.target.value)}>
                  <option value="">Choose the driver</option>
                  {drivers.map((d) => <option key={d.id} value={d.id}>{d.name}{d.status !== 'active' ? `, ${d.status}` : d.manager ? ` (${d.manager})` : ''}</option>)}
                </NativeSelect>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <label htmlFor="sp-date" className="text-xs font-medium text-foreground">Pay date of the short payslip</label>
                  <Input id="sp-date" type="date" max={data?.today} value={payDate} onChange={(e) => setPayDate(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <label htmlFor="sp-amount" className="text-xs font-medium text-foreground">Short by (R)</label>
                  <Input id="sp-amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
                </div>
              </div>
              <div className="space-y-1">
                <label htmlFor="sp-says" className="text-xs font-medium text-foreground">What the driver says is missing</label>
                <Textarea id="sp-says" rows={2} placeholder="e.g. Two shifts on 3 and 4 October are not on the payslip" value={says} onChange={(e) => setSays(e.target.value)} />
              </div>
              <div className="space-y-1">
                <label htmlFor="sp-check" className="text-xs font-medium text-foreground">How you checked it</label>
                <Textarea id="sp-check" rows={2} placeholder="e.g. Both shifts are on record with Uber trips; the payslip has 14 shifts, the record has 16" value={check} onChange={(e) => setCheck(e.target.value)} />
              </div>
              <fieldset className="space-y-2 rounded-md border border-border p-3">
                <legend className="px-1 text-xs font-medium text-foreground">How {releasers} pays it</legend>
                <div className="flex flex-wrap gap-4 text-xs">
                  <label className="flex items-center gap-2"><input type="radio" name="sp-payout" checked={method === 'bank'} onChange={() => setMethod('bank')} /> Into a bank account</label>
                  <label className="flex items-center gap-2"><input type="radio" name="sp-payout" checked={method === 'ewallet'} onChange={() => setMethod('ewallet')} /> eWallet to a cell number</label>
                </div>
                {method === 'ewallet' ? (
                  <div className="space-y-1">
                    <label htmlFor="sp-ewallet" className="text-xs font-medium text-foreground">Cell number for the eWallet</label>
                    <Input id="sp-ewallet" inputMode="tel" value={ewallet} onChange={(e) => setEwallet(e.target.value)} />
                  </div>
                ) : (
                  <div className="grid gap-2 sm:grid-cols-2">
                    <div className="space-y-1">
                      <label htmlFor="sp-bank" className="text-xs font-medium text-foreground">Bank</label>
                      <NativeSelect id="sp-bank" value={bank} onChange={(e) => setBank(e.target.value)}>
                        <option value="">Choose the bank</option>
                        {(data?.banks ?? BANKS_FALLBACK).map((b) => <option key={b} value={b}>{b}</option>)}
                      </NativeSelect>
                    </div>
                    <div className="space-y-1">
                      <label htmlFor="sp-holder" className="text-xs font-medium text-foreground">Account in the name of</label>
                      <Input id="sp-holder" value={holder} onChange={(e) => setHolder(e.target.value)} />
                    </div>
                    <div className="space-y-1">
                      <label htmlFor="sp-account" className="text-xs font-medium text-foreground">Account number</label>
                      <Input id="sp-account" inputMode="numeric" value={account} onChange={(e) => setAccount(e.target.value)} />
                    </div>
                    <div className="space-y-1">
                      <label htmlFor="sp-branch" className="text-xs font-medium text-foreground">Branch code (optional)</label>
                      <Input id="sp-branch" inputMode="numeric" value={branch} onChange={(e) => setBranch(e.target.value)} />
                    </div>
                  </div>
                )}
              </fieldset>
              {error && <ErrorNote>{error}</ErrorNote>}
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={raise.loading}>Cancel</Button>
                <Button variant="brand" onClick={() => void save()} disabled={raise.loading}>
                  {raise.loading ? <Spinner className="mr-1 h-4 w-4" /> : <Coins className="mr-1 h-4 w-4" />} Send to {releasers}
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
